import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingDown, FiTrendingUp, FiDollarSign, FiActivity } = FiIcons;

export default function Analytics() {
  const costOptions = {
    backgroundColor: 'transparent',
    textStyle: { fontFamily: 'monospace' },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(10, 10, 10, 0.9)',
      borderColor: '#333',
      textStyle: { color: '#e5e5e5' },
      axisPointer: { type: 'shadow' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { lineStyle: { color: '#404040' } },
      axisLabel: { color: '#737373' }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#262626', type: 'dashed' } },
      axisLabel: { color: '#737373', formatter: '${value}' }
    },
    series: [
      {
        name: 'Transit Cost',
        type: 'bar',
        barWidth: '40%',
        data: [4.5, 4.5, 6.0, 4.5, 8.5, 0, 0],
        itemStyle: {
          color: '#34d399', // emerald-400
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: { color: '#10b981' } // emerald-500
        }
      }
    ]
  };

  const efficiencyOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      axisLine: { lineStyle: { color: '#404040' } },
      axisLabel: { color: '#737373', fontFamily: 'monospace' }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#262626' } },
      axisLabel: { color: '#737373', formatter: '{value}m' }
    },
    series: [
      {
        name: 'Avg Commute Time',
        type: 'line',
        smooth: true,
        data: [45, 42, 46, 38],
        lineStyle: { color: '#60a5fa', width: 3 }, // blue-400
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(96, 165, 250, 0.3)' },
              { offset: 1, color: 'rgba(96, 165, 250, 0)' }
            ]
          }
        },
        symbol: 'none'
      }
    ]
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className="space-y-6"
    >
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-neutral-100">Metrics & Telemetry</h2>
        <p className="text-neutral-400 mt-1 font-mono text-sm">Weekly commute overview</p>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Spent', value: '$28.00', trend: '-12%', icon: FiDollarSign, good: true },
          { label: 'Avg Time', value: '42m', trend: '-3m', icon: FiActivity, good: true },
          { label: 'Carbon Saved', value: '14kg', trend: '+2kg', icon: FiTrendingUp, good: true },
          { label: 'Delays', value: '2', trend: '+1', icon: FiTrendingDown, good: false },
        ].map((kpi, i) => (
          <div key={i} className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-4 flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-neutral-800/50 rounded-lg text-neutral-400">
                <SafeIcon icon={kpi.icon} />
              </div>
              <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                kpi.good ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
              }`}>
                {kpi.trend}
              </span>
            </div>
            <span className="text-2xl font-bold text-neutral-200 mb-1">{kpi.value}</span>
            <span className="text-[10px] text-neutral-500 uppercase tracking-widest font-mono">{kpi.label}</span>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-mono text-sm uppercase tracking-widest text-neutral-400 mb-6">Cost Breakdown (Current Week)</h3>
          <div className="h-[250px] w-full">
            <ReactECharts option={costOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-mono text-sm uppercase tracking-widest text-neutral-400 mb-6">Time Efficiency Trend</h3>
          <div className="h-[250px] w-full">
            <ReactECharts option={efficiencyOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}